import { createControlId, HpRemoveControlGroupActionProps } from '@avantia/questionset-model';
import { BaseSyntheticEvent } from 'react';
import { getCustomFunction } from '../../customFunctionRegistry';
import { HpClientQuestion } from '../../types';
import { HpEventDetail } from '../../types/inputControlTypes';
import {
  HpCreateHandlersProps,
  HpCreateHandlersResponse,
  HpModalConfirmCustomHandlerFunction,
  HpModalWidgetStateProps,
  HpShowModalWidgetProps
} from '../../types/modalWidgetTypes';

export function createHandlers({
  cancelOnClickHandler,
  confirmOnClickHandler,
  getHtmlElement,
  getState,
  pageProps,
  questionSet,
  setState,
  showConfirmModal,
  showInformationModal,
  triggerModalActionEvent
}: HpCreateHandlersProps): HpCreateHandlersResponse {
  const hideModal = () => setState(createInitialState());

  const showModal = (widgetProps: HpShowModalWidgetProps): void => {
    const {
      customButtons,
      id,
      modalBodyClass,
      modalClass,
      modalCopyText,
      modalFooterClass,
      modalHeaderClass,
      onCancelClick,
      onConfirmClick,
      question
    } = widgetProps;
    const newState: HpModalWidgetStateProps = {
      show: true,
      question,
      onConfirmClick,
      id,
      modalCopyText,
      modalClass,
      modalHeaderClass,
      modalBodyClass,
      modalFooterClass,
      customButtons
    };

    setState(newState);

    cancelOnClickHandler.current = onCancelClick || null;
    confirmOnClickHandler.current = onConfirmClick || null;
  };

  const { id, modalCopyText, question, show } = getState();
  if (!question || !id || !modalCopyText) {
    return { showModal };
  }

  const detail: HpEventDetail = { type: 'button-action', question };

  const handleConfirm = (event: BaseSyntheticEvent): void => {
    const { current: clickHandler } = confirmOnClickHandler || {};
    if (clickHandler && question) {
      const { answers, questionSetId } = questionSet;
      const customHandler: HpModalConfirmCustomHandlerFunction | undefined = getCustomConfirmHandler({
        questionSetId,
        question
      });

      if (customHandler) {
        const handler: VoidFunction = () => clickHandler(event as any, detail);
        return customHandler({
          event,
          answers,
          question,
          questionSet,
          pageProps,
          handleConfirm: handler,
          hideModal,
          showConfirmModal,
          showInformationModal
        });
      }

      const result = clickHandler(event as any, detail) as string | void;
      if (result === 'dismiss=false') {
        return;
      }
    }

    hideModal();
  };

  const modalId = createControlId({ questionId: `${id}-modal` });
  const handleCancel = (ev?: BaseSyntheticEvent) => {
    if (!show) {
      return;
    }

    let event: BaseSyntheticEvent;
    if (!ev) {
      const closeButtonSelector = `#${modalId.replace('~', '\\~')} .modal-header > .close`;
      const target: HTMLElement = getHtmlElement(closeButtonSelector);
      event = { target, type: 'click' } as any;
    } else {
      event = ev;
    }

    const { current: clickHandler } = cancelOnClickHandler || {};
    if (clickHandler) {
      clickHandler(event as any, detail);
    }

    triggerModalActionEvent({ question, getEvent: () => event, buttonType: 'cancel' });
    hideModal();
  };

  return { showModal, handleConfirm, handleCancel, modalId };
}

export function createInitialState(): HpModalWidgetStateProps {
  return { show: false };
}

export interface HpGetCustomConfirmHandlerProps {
  question: HpClientQuestion;
  questionSetId: string;
}

function getCustomConfirmHandler({
  question
}: HpGetCustomConfirmHandlerProps): HpModalConfirmCustomHandlerFunction | undefined {
  const { data } = question;
  const { dependentActions } = data || {};
  if (dependentActions) {
    const dependentAction = dependentActions.filter((d) => d.action === 'remove-control-group')[0];
    const { props } = dependentAction || {};
    const { customHandler } = (props as HpRemoveControlGroupActionProps) || {};
    if (customHandler) {
      return getCustomFunction('', customHandler) as HpModalConfirmCustomHandlerFunction;
    }
  }

  return undefined;
}
